import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { searchAddressSuggestionsApi, type AddressSuggestion } from '../../api/addressApi';

type AddressAutocompleteOption = AddressSuggestion | {
  id: '__no-results__';
  source: 'empty';
  label: string;
};

type AddressAutocompleteFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onSelectionChange?: (isSelected: boolean) => void;
  onSuggestionChange?: (option: AddressSuggestion | null) => void;
  onEmptyActionClick?: (inputValue: string) => void;
  extraOptions?: AddressSuggestion[];
  emptyOptionLabel?: string;
  emptyOptionClickable?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  required?: boolean;
  closeSuggestions?: boolean;
};

const AddressAutocompleteField: React.FC<AddressAutocompleteFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onSelectionChange,
  onSuggestionChange,
  onEmptyActionClick,
  extraOptions = [],
  emptyOptionLabel,
  emptyOptionClickable = true,
  error = false,
  helperText,
  placeholder,
  required = false,
  closeSuggestions = false,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasUserTyped, setHasUserTyped] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (closeSuggestions) {
      setIsOpen(false);
    }
  }, [closeSuggestions]);

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setOptions([]);
      setLoading(false);
      setHasSearched(false);
      return undefined;
    }

    setLoading(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await searchAddressSuggestionsApi(trimmed);
        const rawOptions = Array.isArray(response?.data?.data) ? response.data.data : [];
        const memoryOptions = extraOptions.filter((option) =>
          option.label.toLowerCase().includes(trimmed.toLowerCase())
        );
        const mergedOptions = [...memoryOptions, ...rawOptions];
        const uniqueOptions = mergedOptions.filter((option, index, items) => {
          return items.findIndex((item) => item.label === option.label) === index;
        });
        setOptions(
          uniqueOptions.slice(0, 5)
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [extraOptions, inputValue]);

  const selectedOption = useMemo(() => {
    const availableOptions = [...extraOptions, ...options];
    return availableOptions.find((option) => option.label === value) || null;
  }, [extraOptions, options, value]);
  const trimmedInputValue = inputValue.trim();
  const hasExactSelectedValue = Boolean(
    selectedOption &&
    typeof selectedOption !== 'string' &&
    selectedOption.label.trim() === trimmedInputValue
  );
  const showEmptyResult =
    trimmedInputValue.length >= 3 &&
    hasUserTyped &&
    hasSearched &&
    !loading &&
    options.length === 0 &&
    !hasExactSelectedValue;
  const autocompleteOptions: AddressAutocompleteOption[] = showEmptyResult
    ? [{ id: '__no-results__', source: 'empty', label: emptyOptionLabel || t('addAds.addressNoResults') }]
    : options;

  return (
    <Autocomplete
      freeSolo
      options={autocompleteOptions}
      value={selectedOption}
      inputValue={inputValue}
      open={isOpen && !closeSuggestions}
      slotProps={{
        popper: {
          sx: { zIndex: 1400 },
        },
      }}
      onOpen={() => {
        if (!closeSuggestions) {
          setIsOpen(true);
        }
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      onInputChange={(_, nextValue, reason) => {
        setInputValue(nextValue);
        onChange(nextValue);

        if (!closeSuggestions && nextValue.trim().length >= 2) {
          setIsOpen(true);
        }

        if (reason === 'input' || reason === 'clear') {
          setHasUserTyped(reason === 'input');
          if (reason === 'input') {
            setHasSearched(false);
          }
          if (reason === 'clear') {
            setHasSearched(false);
          }
          onSelectionChange?.(false);
          onSuggestionChange?.(null);
        }
      }}
      onChange={(_, nextValue) => {
        if (!nextValue) {
          setInputValue('');
          setOptions([]);
          setIsOpen(false);
          setHasSearched(false);
          setHasUserTyped(false);
          onSelectionChange?.(false);
          onSuggestionChange?.(null);
          onChange('');
          return;
        }

        if (typeof nextValue !== 'string' && nextValue?.source === 'empty') {
          const preservedValue = trimmedInputValue || '';
          setInputValue(preservedValue);
          setOptions([]);
          setIsOpen(false);
          setHasUserTyped(false);
          onSelectionChange?.(false);
          onSuggestionChange?.(null);
          if (emptyOptionClickable) {
            onEmptyActionClick?.(trimmedInputValue);
          }
          return;
        }

        const nextLabel =
          typeof nextValue === 'string'
            ? nextValue
            : nextValue?.label || '';
        setInputValue(nextLabel);
        setHasSearched(false);
        setHasUserTyped(false);
        setIsOpen(false);
        onSelectionChange?.(typeof nextValue !== 'string' && Boolean(nextValue?.label));
        onSuggestionChange?.(typeof nextValue === 'string' ? null : nextValue || null);
        onChange(nextLabel);
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, currentValue) => option.id === currentValue.id}
      filterOptions={(items) => items}
      loading={loading}
      renderOption={(props, option) => {
        const isEmptyOption = typeof option !== 'string' && option.source === 'empty';
        const { style, onMouseDown, onClick, ...restProps } = props;

        return (
          <li
            {...restProps}
            onMouseDown={(event) => {
              if (isEmptyOption && emptyOptionClickable) {
                event.preventDefault();
                return;
              }

              onMouseDown?.(event);
            }}
            onClick={(event) => {
              if (isEmptyOption && emptyOptionClickable) {
                event.stopPropagation();
                onEmptyActionClick?.(trimmedInputValue);
                return;
              }

              onClick?.(event);
            }}
            style={{
              ...(style || {}),
              color: isEmptyOption && emptyOptionClickable ? '#1f5fbf' : (isEmptyOption ? 'rgba(0, 0, 0, 0.6)' : style?.color),
              opacity: isEmptyOption ? 1 : style?.opacity,
              fontWeight: isEmptyOption && emptyOptionClickable ? 600 : undefined,
              textDecoration: isEmptyOption && emptyOptionClickable ? 'underline' : undefined,
              cursor: isEmptyOption && emptyOptionClickable ? 'pointer' : undefined,
              WebkitTextFillColor: isEmptyOption && emptyOptionClickable ? '#1f5fbf' : undefined,
            }}
          >
            {typeof option === 'string' ? option : option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={name}
          required={required}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AddressAutocompleteField;
